import * as React from "react";
import { useEffect, useState, useContext } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { AuthContext } from "contexts/authContext";
import CustomTooltip from "components/Custom/CustomTooltip";

// react-bootstrap components
import {
  Card,
  Container,
  Row,
  Form,
  Col,
  Button,
  Spinner,
} from "react-bootstrap";
import {
  getClientEntitlements,
  updateClientEntitlements,
} from "graphql/queries.js";
import _ from "lodash";
import { API, graphqlOperation } from "aws-amplify";

function ManageEntitlements() {
  // Hooks
  const notify = useContext(NotificationAlertContext);
  const history = useHistory();

  // Contexts
  const currentUser = useContext(AuthContext);

  const { clientId } = useParams();
  const [clientData, setClientData] = useState(null);

  const [showLoader, setShowLoader] = useState(false);
  const [showError, setShowError] = useState(false);

  const [validated, setValidated] = React.useState(false);

  const [channelReportingValue, setChannelReportingValue] = useState(0);
  const [channelArchivingValue, setChannelArchivingValue] = useState(0);

  const [channelReportingUsed, setChannelReportingUsed] = useState(0);
  const [channelArchivingUsed, setChannelArchivingUsed] = useState(0);

  const [valueChanged, setValueChanged] = useState(false);

  const regex_check = "^[0-9]+$";

  const fetchClientEntitlements = async () => {
    try {
      setShowLoader(true);
      const params = {
        client_id: clientId,
      };
      const response = await API.graphql(
        graphqlOperation(getClientEntitlements, params)
      );
      if (response.data?.getClientEntitlements?.success) {
        const clientData = _.get(
          response,
          "data.getClientEntitlements.body",
          []
        );
        const entitlements = _.get(clientData, "entitlements", []);
        for (let index = 0; index < entitlements.length; index++) {
          if (entitlements[index].type == "CHANNEL_METRICS") {
            setChannelReportingValue(entitlements[index].allotment);
            setChannelReportingUsed(entitlements[index].used);
          } else if (entitlements[index].type == "CHANNEL_ARCHIVING") {
            setChannelArchivingValue(entitlements[index].allotment);
            setChannelArchivingUsed(entitlements[index].used);
          }
        }
        setClientData(clientData);
      } else {
        console.error(response.data?.getClientEntitlements?.message);
      }
      return response.data?.getClientEntitlements?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const regex_tester = new RegExp(regex_check);

      if (
        !regex_tester.test(String(channelReportingValue)) ||
        !regex_tester.test(String(channelArchivingValue))
      ) {
        notify("danger", "Please enter all fields with a digit.");
        return;
      }

      if (
        channelReportingValue < channelReportingUsed
      ) {
        notify(
          "danger",
          "The entitlement value must be equal or greater than what is currently being used."
        );
        return;
      }

      const entitlement_list = [];
      entitlement_list.push({
        type: "CHANNEL_METRICS",
        allotment: channelReportingValue,
      });
      entitlement_list.push({
        type: "CHANNEL_ARCHIVING",
        allotment: channelArchivingValue,
      });

      const params = {
        client_id: clientId,
        license_id: clientData.license_id,
        entitlements: JSON.stringify(entitlement_list),
      };

      notify("info", "Sending entitlement update request.");
      const response = await API.graphql(
        graphqlOperation(updateClientEntitlements, params)
      );

      if (response.data?.updateClientEntitlements?.success) {
        notify("success");
        setTimeout(() => {
          history.push({
            pathname: `/admin/clients/${clientId}`,
          });
        }, 3000);
      } else {
        notify("danger");
        console.error(response.data?.updateClientEntitlements?.message);
      }
      return response.data?.updateClientEntitlements?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  useEffect(() => {
    async function fetchData() {
      const isSuccess = await fetchClientEntitlements();
      if (!isSuccess) {
        notify("danger");
        history.goBack();
      }
    }
    fetchData();
  }, []);

  if (showLoader) return <Spinner animation="grow" />;

  if (showError)
    return `Could not load the page at this moment. Please try again later.`;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Manage Entitlements</h2>
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>Client Name: {clientData?.client_name}</h5>
            <h5>Client Id: {clientData?.client_id}</h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <Form noValidate validated={validated}>
                  <Form.Group>
                    <Row className="align-items-end">
                      <Col md="4">
                        <label>
                          Channel Monitoring (Channels Currently Enabled: {channelReportingUsed})
                        </label>
                        <sup>
                          <CustomTooltip
                            id={`channel-reporting-tooltip`}
                            value="Channel Reporting entitlements allow channels to report metrics to Mirth Command Center"
                          >
                            <i
                              className="fa fa-info-circle"
                              style={{ marginLeft: "1px" }}
                            ></i>
                          </CustomTooltip>
                        </sup>
                        <Form.Control
                          disabled={currentUser.groupName !== "internal"}
                          readOnly={currentUser.groupName !== "internal"}
                          required
                          id="validationChannelReportingId"
                          type="text"
                          name="channelReportingValue"
                          value={channelReportingValue}
                          minLength="1"
                          maxLength="5"
                          pattern={regex_check}
                          onChange={(e) => {
                            setValidated(true);
                            setValueChanged(true);
                            setChannelReportingValue(e.target.value);
                          }}
                        ></Form.Control>
                        <Form.Control.Feedback type="invalid">
                          Value must be a digit
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <label>Channel Archiving Allocation in GB (Channels Currently Enabled: {channelArchivingUsed})</label>
                        <sup>
                          <CustomTooltip
                            id={`channel-archiving-tooltip`}
                            value="Channel Archiving entitlements allow channels to archive messages to Mirth Command Center"
                          >
                            <i
                              className="fa fa-info-circle"
                              style={{ marginLeft: "1px" }}
                            ></i>
                          </CustomTooltip>
                        </sup>
                        <Form.Control
                          disabled={currentUser.groupName !== "internal"}
                          readOnly={currentUser.groupName !== "internal"}
                          required
                          id="validationChannelArchivingId"
                          type="text"
                          name="channelArchivingValue"
                          value={channelArchivingValue}
                          minLength="1"
                          maxLength="5"
                          pattern={regex_check}
                          onChange={(e) => {
                            setValidated(true);
                            setValueChanged(true);
                            setChannelArchivingValue(e.target.value);
                          }}
                        ></Form.Control>
                        <Form.Control.Feedback type="invalid">
                          Value must be a digit
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="3">
                        {currentUser.groupName === "internal" && (
                          <Button
                            variant="primary"
                            disabled={!valueChanged}
                            className="mr-2"
                            onClick={() => {
                              handleSubmit();
                            }}
                          >
                            Save Changes
                          </Button>
                        )}
                        <Button
                          variant="default"
                          className="btn-outline"
                          onClick={() => {
                            history.push({
                              pathname: `/admin/clients/${clientId}`,
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ManageEntitlements;
